/**
 * PermissionRequiredRouter: Handle routing to a page based on if a user has a
 * specific permission.
 *
 * If a user fails the permission check then they will see an error message
 * stating they're not allowed to access it.
 *
 * If the user is logged out they will be redirected to the login page (and
 * they will be returned upon a successful login attempt).
 *
 * It's not possible to perform this check when server-side rendering so this
 * route will only show a loading spinner on server-side render, instead.
 */
import * as React from "react";
import {Route} from "react-router-dom";

import LoginRequired from "@khan/component-screens/login-required";
import {PermissionProvider, type Permission} from "@khan/permissions";
import {PermissionsInfo, RequestInfo} from "@khan/shared-core";

import AsyncRouteHandler from "./async-route-handler.tsx";
import ErrorLoadingPage from "./errors/error-loading-page.tsx";
import PermissionDenied from "./errors/permission-denied.tsx";
import RouteLoadSpinner from "./route-load-spinner.tsx";
import type {CommonRouteProps} from "./types.ts";

type Props = CommonRouteProps & {
    // The permission to check.
    permission: Permission;
    // Can also allow this route for dev server.
    devServerAlsoAllowed?: boolean;
};

const PermissionRequiredRoute = (props: Props): React.ReactElement => {
    const permittedForDev =
        props.devServerAlsoAllowed && RequestInfo.IS_DEV_SERVER;

    return (
        <Route
            path={props.path}
            render={() => {
                // If the user isn't logged-in and the route isn't explicitly
                // allowed on the dev server then this permission check
                // will definitely fail, so check these things first.
                if (!PermissionsInfo.isLoggedIn && !permittedForDev) {
                    return <LoginRequired />;
                }

                return (
                    <PermissionProvider permission={props.permission}>
                        {({loading, error, hasPermission}) => {
                            if (loading) {
                                return (
                                    <RouteLoadSpinner
                                        template={props.template}
                                    />
                                );
                            }
                            if (error) {
                                return <ErrorLoadingPage />;
                            }
                            if (hasPermission || permittedForDev) {
                                return (
                                    <AsyncRouteHandler
                                        component={props.component}
                                        template={props.template}
                                    />
                                );
                            }
                            // The user doesn't have permission to view
                            // this route so we show an error instead.
                            return <PermissionDenied />;
                        }}
                    </PermissionProvider>
                );
            }}
        />
    );
};

export default PermissionRequiredRoute;
